import React, { createContext, useContext, useReducer } from 'react'
import { type DisplaySettings } from './components/DisplayCustomizationDrawer'
import { getDefaultSettings } from './components/helper'

export enum ReportsActionType {
  SET_SELECTED_TREND = 'SET_SELECTED_TREND',
  SET_IS_FULLSCREEN = 'SET_IS_FULLSCREEN',
  SET_DISPLAY_SETTINGS_CHANGED = 'SET_DISPLAY_SETTINGS_CHANGED',
  SET_DISPLAY_SETTINGS = 'SET_DISPLAY_SETTINGS',
}

interface ReportsState {
  selectedTrend: number
  isFullScreen: boolean
  displaySettingsChanged: boolean
  displaySettings: DisplaySettings
}

type ReportsAction =
  { type: ReportsActionType.SET_SELECTED_TREND, payload: number } |
  { type: ReportsActionType.SET_IS_FULLSCREEN, payload: boolean } |
  { type: ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED, payload: boolean } |
  { type: ReportsActionType.SET_DISPLAY_SETTINGS, payload: DisplaySettings }

const initialState: ReportsState = {
  selectedTrend: 1,
  isFullScreen: false,
  displaySettingsChanged: false,
  displaySettings: getDefaultSettings()
}

const reportsReducer: React.Reducer<ReportsState, ReportsAction> = (state, action) => {
  switch (action.type) {
    case ReportsActionType.SET_SELECTED_TREND:
      return { ...state, selectedTrend: action.payload }
    case ReportsActionType.SET_IS_FULLSCREEN:
      return { ...state, isFullScreen: action.payload }
    case ReportsActionType.SET_DISPLAY_SETTINGS_CHANGED:
      return { ...state, displaySettingsChanged: action.payload }
    case ReportsActionType.SET_DISPLAY_SETTINGS:
      return { ...state, displaySettings: action.payload }
    default:
      return state
  }
}

interface ReportsContextType extends ReportsState {
  dispatch: React.Dispatch<ReportsAction>
}

const ReportsContext = createContext<ReportsContextType | undefined>(undefined)

export const ReportsProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const [state, dispatch] = useReducer(reportsReducer, initialState)

  return (
    <ReportsContext.Provider value={{ ...state, dispatch }}>
      {children}
    </ReportsContext.Provider>
  )
}

export const useReportsContext = (): ReportsContextType => {
  const context = useContext(ReportsContext)
  if (!context) throw new Error('useReportsContext must be used within ReportsProvider')
  return context
}
